import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, MenuItem, styled, Collapse } from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';

import { FHTooltip } from '../../base';
import { FORTA_BLUE } from '../../../utils/colors';
import { areUserRolesIn } from '../../../helpers';

import type { MouseEvent } from 'react';
import type { Roles } from '../../../constants';

const StyledMenuIcon = styled('img')({
  width: 24,
  height: 24,
  marginRight: '0.5em',
  filter:
    'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(13%) hue-rotate(270deg) brightness(106%) contrast(104%)',
});

const itemStyle = {
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: 24,
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '24px 20px',
  borderRadius: 1,
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: FORTA_BLUE.light1,
  },
};

interface NestedItemProps {
  title: string;
  icon: {
    url: string;
    width: number;
    height: number;
  };
  iconStyle?: any;
  nestedItems: Array<{
    title: string;
    to: string;
    icon?: any;
    roles: Roles[];
  }>;
  dataTestId?: string;
  isCollapsed: boolean;
  sx?: any;
}

const NestedNavItem = (props: NestedItemProps) => {
  const { title, icon, iconStyle, dataTestId, isCollapsed, nestedItems, sx = {} } = props;
  const { user } = useSelector((state: any) => state.auth);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const activeStyle = {
    backgroundColor: FORTA_BLUE.light1,
  };

  // useEffect to check location changes and update isOpen state
  useEffect(() => {
    // Check if any nested item's route matches the current location
    const isOpen = nestedItems.some((item) => location.pathname.includes(item.to));
    setIsOpen(isOpen);
  }, [location.pathname, nestedItems]);

  const handleRouteChange = (e: MouseEvent, to: string) => {
    if (to === '/treatment-plan' && location.pathname.includes(to)) {
      e.preventDefault();
    }
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Box sx={{ ...sx }}>
      <Box
        onClick={toggleOpen}
        sx={{
          ...itemStyle,
          borderRadius:
            isOpen && !nestedItems.some((item) => location.pathname.includes(item.to))
              ? '8px 8px 0 0'
              : 1,
          justifyContent: 'flex-start',
          '& img': { ...iconStyle },
          ...(isOpen && activeStyle),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <img
              src={icon.url}
              alt="nav-icon"
              width={isCollapsed ? 30 : icon.width}
              height={isCollapsed ? 30 : icon.height}
              style={{
                transition: 'all 0.3s ease-in-out',
                marginLeft: isCollapsed ? '7px' : '-8px',
              }}
            />

            {!isCollapsed && (
              <Typography
                sx={{
                  color: 'white',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '15px',
                }}
              >
                {title}
              </Typography>
            )}
          </Box>
          <Box display={{ display: 'flex', alignItems: 'center' }}>
            {nestedItems &&
              nestedItems.length > 0 &&
              !isCollapsed &&
              (isOpen ? (
                <ExpandMore sx={{ color: 'white', transition: 'transform 0.3s' }} />
              ) : (
                <ChevronRight sx={{ color: 'white', transition: 'transform 0.3s' }} />
              ))}
          </Box>
        </Box>
      </Box>
      {nestedItems && nestedItems.length > 0 && (
        <Collapse
          in={isOpen}
          timeout="auto"
          unmountOnExit
          sx={{
            width: '100%',
            backgroundColor:
              isOpen && !nestedItems.some((item) => location.pathname.includes(item.to))
                ? FORTA_BLUE.light1
                : 'transparent',
            borderRadius: '0 0 8px 8px',
            paddingBottom: 1.5,
          }}
        >
          <Box>
            {nestedItems.map((nestedItem, idx) =>
              areUserRolesIn(user?.roles, nestedItem.roles) ? (
                <FHTooltip
                  key={idx}
                  tooltipBorderRadius="8px"
                  title={nestedItem.title}
                  tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                  placement="right-end"
                >
                  <NavLink
                    key={idx}
                    data-testid={dataTestId}
                    to={nestedItem.to}
                    onClick={
                      nestedItem.to
                        ? (e: MouseEvent) => handleRouteChange(e, nestedItem.to)
                        : undefined
                    }
                    style={{
                      textDecoration: 'none',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',

                      marginTop: idx === 0 ? 10 : 0,
                      marginBottom: idx === nestedItems.length - 1 ? -10 : 10,
                    }}
                  >
                    {({ isActive }) => (
                      <MenuItem
                        onClick={() => setIsOpen(true)}
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          ...(isActive && activeStyle),
                          alignItems: 'center',
                          width: '100%',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            backgroundColor: FORTA_BLUE.light1,
                            fontWeight: 'bold',
                          },
                          borderRadius: 1,
                          paddingTop: '12px',
                          paddingBottom: '12px',
                          paddingLeft: isCollapsed ? '20px' : '40px',
                          paddingRight: '20px',
                        }}
                      >
                        {nestedItem.icon ? (
                          <StyledMenuIcon
                            sx={{
                              marginLeft: isCollapsed ? '8px' : '-8px',
                            }}
                            src={nestedItem.icon}
                            alt="nav-icon"
                          />
                        ) : (
                          //Round icon with the first letter of the title
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: FORTA_BLUE.light1,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              color: 'white',
                              fontSize: '12px',
                              marginRight: '0.5em',
                              border: '1px solid white',
                            }}
                          >
                            {nestedItem.title.charAt(0)}
                          </Box>
                        )}
                        {!isCollapsed && (
                          <Typography
                            sx={{
                              color: 'white',
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {nestedItem.title}
                          </Typography>
                        )}
                      </MenuItem>
                    )}
                  </NavLink>
                </FHTooltip>
              ) : null,
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default NestedNavItem;
